import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchApiKeys, createApiKey, deleteApiKey, rotateApiKey, clearNewKey } from '../../../store/slices/apiKeySlice';
import Modal from '../../../components/Modal';

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function formatRelative(d) {
  if (!d) return 'Never';
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// Keys work across every product (AI + Live Streaming); the gateway gates
// mutating calls (e.g. creating or ending a stream) on `write`.
const ACCESS = [
  { id: 'full', label: 'Full access', desc: 'AI requests, create and end streams, settings', permissions: { read: true, write: true } },
  { id: 'read', label: 'Read only', desc: 'AI requests, list streams, usage — cannot create or change anything', permissions: { read: true, write: false } },
];

function keyAccess(k) {
  return k.permissions?.write ? 'full' : 'read';
}

function CreateKeyModal({ onClose }) {
  const dispatch = useDispatch();
  const { loading } = useSelector(s => s.apiKeys);
  const [name, setName] = useState('');
  const [expiry, setExpiry] = useState('never');
  const [access, setAccess] = useState('full');

  const expiresAt = () => {
    if (expiry === 'never') return undefined;
    const d = new Date();
    d.setDate(d.getDate() + { '30d': 30, '90d': 90, '1y': 365 }[expiry]);
    return d.toISOString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    const r = await dispatch(createApiKey({
      name: name.trim(),
      permissions: ACCESS.find(a => a.id === access).permissions,
      expiresAt: expiresAt(),
    }));
    if (!r.error) onClose();
  };

  return (
    <Modal onClose={onClose}>
      <div className="modal-head" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div className="modal-title">Create API key</div>
          <p className="modal-sub">One key works across every Cybernate product — AI and Live Streaming — and bills the same credits.</p>
        </div>
        <button type="button" className="modal-close" onClick={onClose}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
        </button>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          <div className="field">
            <label>Key name</label>
            <input type="text" required placeholder="e.g. production-main" value={name}
              onChange={e => setName(e.target.value)} autoFocus />
          </div>

          <div className="field">
            <label>Expiry</label>
            <select value={expiry} onChange={e => setExpiry(e.target.value)}>
              <option value="never">Never expires</option>
              <option value="30d">30 days</option>
              <option value="90d">90 days</option>
              <option value="1y">1 year</option>
            </select>
          </div>

          <div className="field">
            <label>Access</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
              {ACCESS.map(a => (
                <button key={a.id} type="button" onClick={() => setAccess(a.id)} style={{
                  padding: '10px 14px', borderRadius: 'var(--r-md)', cursor: 'pointer', textAlign: 'left',
                  border: `1px solid ${access === a.id ? 'rgba(139,124,255,0.5)' : 'var(--line-2)'}`,
                  background: access === a.id ? 'rgba(139,124,255,0.12)' : 'transparent',
                  transition: 'all 150ms',
                }}>
                  <div style={{ fontSize: 13, color: access === a.id ? 'var(--accent)' : 'var(--fg)' }}>{access === a.id ? '✓ ' : ''}{a.label}</div>
                  <div style={{ fontSize: 12, color: 'var(--fg-dim)', marginTop: 2 }}>{a.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div style={{ padding: '10px 14px', background: 'rgba(139,124,255,0.06)', border: '1px solid rgba(139,124,255,0.15)', borderRadius: 'var(--r-md)', fontSize: 12, color: 'var(--fg-mid)', lineHeight: 1.6 }}>
            Key shown <strong style={{ color: 'var(--fg)' }}>once</strong> after creation — save it immediately.
          </div>
        </div>

        <div className="modal-foot">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={loading || !name.trim()}>
            {loading ? <span className="btn-loader" /> : 'Generate key →'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function NewKeyReveal({ keyValue, onDismiss }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(keyValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div style={{ padding: '16px 20px', background: 'rgba(107,255,184,0.06)', border: '1px solid rgba(107,255,184,0.2)', borderRadius: 'var(--r-md)', marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--signal)' }}>Key created — copy it now</span>
        <button onClick={onDismiss} style={{ fontSize: 11, color: 'var(--fg-dim)', fontFamily: 'var(--font-mono)' }}>dismiss</button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 'var(--r-sm)', padding: '10px 14px' }}>
        <code style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg)', flex: 1, wordBreak: 'break-all' }}>{keyValue}</code>
        <button className="btn btn-ghost" style={{ padding: '5px 12px', fontSize: 11, flexShrink: 0 }} onClick={copy}>
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </div>
      <p style={{ fontSize: 11, color: 'var(--fg-dim)', marginTop: 8, fontFamily: 'var(--font-mono)', letterSpacing: '0.04em' }}>
        You won't be able to see this key again.
      </p>
    </div>
  );
}

export default function ApiKeys() {
  const dispatch = useDispatch();
  const { keys, loading, error, newKey, lastFetched } = useSelector(s => s.apiKeys);
  const [showCreate, setShowCreate] = useState(false);
  const [revokeConfirm, setRevokeConfirm] = useState(null);
  useEffect(() => {
    const STALE = 5 * 60 * 1000;
    if (!lastFetched || Date.now() - lastFetched > STALE) dispatch(fetchApiKeys());
  }, []); // eslint-disable-line

  const handleRevoke = async (id) => {
    await dispatch(deleteApiKey(id));
    setRevokeConfirm(null);
  };

  return (
    <div className="dash-page">
      <div className="dash-page-header">
        <div>
          <p className="dash-sub">WORKSPACE</p>
          <h1 className="dash-page-title">API Keys</h1>
          <p className="dash-page-desc">One key works across AI and Live Streaming. Send it as the <code>X-Api-Key</code> header.</p>
        </div>
        <button className="btn btn-primary" style={{ flexShrink: 0, marginTop: 4 }} onClick={() => setShowCreate(true)}>
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M5.5 1v9M1 5.5h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          Create key
        </button>
      </div>

      {newKey && <NewKeyReveal keyValue={newKey} onDismiss={() => dispatch(clearNewKey())} />}
      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="dash-card">
        <div className="dash-card-head">
          <div>
            <div className="dash-card-title">Active keys</div>
            <div className="dash-card-desc">{keys.length} key{keys.length !== 1 ? 's' : ''} in workspace</div>
          </div>
        </div>
        {loading && !keys.length ? (
          <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[1,2,3].map(i => <div key={i} style={{ height: 44, background: 'var(--line)', borderRadius: 6, animation: 'shimmer 1.4s ease-in-out infinite' }} />)}
          </div>
        ) : !keys.length ? (
          <div className="dash-empty">
            <div className="dash-empty-icon">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="7.5" cy="8.5" r="4.5" stroke="currentColor" strokeWidth="1.4"/><path d="M10.5 11.5l6 6M14.5 16l3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
            </div>
            <div className="dash-empty-title">No API keys yet</div>
            <p className="dash-empty-sub">Create your first key to start making API calls from your applications.</p>
            <button className="btn btn-primary" onClick={() => setShowCreate(true)}>Create API key →</button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Key</th>
                  <th>Access</th>
                  <th>Created</th>
                  <th>Last used</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {keys.map(k => (
                  <tr key={k.id}>
                    <td className="td-name">{k.name}</td>
                    <td className="td-mono" style={{ color: 'var(--fg-mid)' }}>
                      {k.maskedKey || '•••••'}
                    </td>
                    <td>
                      <span className={`badge ${keyAccess(k) === 'full' ? 'badge-live' : 'badge-test'}`}>
                        {keyAccess(k) === 'full' ? 'full access' : 'read only'}
                      </span>
                    </td>
                    <td className="td-mono">{formatDate(k.createdAt)}</td>
                    <td className="td-mono" style={{ color: 'var(--fg-dim)' }}>{formatRelative(k.lastUsedAt)}</td>
                    <td>
                      {k.isActive === false
                        ? <span className="badge badge-test">inactive</span>
                        : <span className="badge badge-live">active</span>}
                    </td>
                    <td className="td-action">
                      {k.isActive === false ? null : revokeConfirm === k.id ? (
                        <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button onClick={() => handleRevoke(k.id)} style={{ fontSize: 11, color: '#ff6b6b', padding: '4px 8px', borderRadius: 'var(--r-sm)' }}>Confirm</button>
                          <button onClick={() => setRevokeConfirm(null)} style={{ fontSize: 11, color: 'var(--fg-dim)', padding: '4px 8px', borderRadius: 'var(--r-sm)' }}>Cancel</button>
                        </span>
                      ) : (
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          <button onClick={() => dispatch(rotateApiKey(k.id))} style={{ fontSize: 12, color: 'var(--fg-dim)', padding: '4px 8px', borderRadius: 'var(--r-sm)' }}>Rotate</button>
                          <button onClick={() => setRevokeConfirm(k.id)} style={{ fontSize: 12, color: 'var(--fg-dim)', padding: '4px 8px', borderRadius: 'var(--r-sm)' }}>Revoke</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="dash-card-foot" style={{ fontSize: 12, color: 'var(--fg-dim)', display: 'flex', justifyContent: 'space-between' }}>
          <span>Secret keys are masked after creation.</span>
          <a href="https://docs.cybernate.ai/auth" style={{ color: 'var(--accent)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>Auth docs →</a>
        </div>
      </div>

      {showCreate && <CreateKeyModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
